import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Star, MessageSquare, ThumbsUp, Clock, 
  TrendingUp, Award, User, Send
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Star Rating Component
export const StarRating = ({ rating, size = 'md', interactive = false, onChange }) => {
  const [hovered, setHovered] = useState(0);
  
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6'
  };
  
  const stars = [1, 2, 3, 4, 5];
  
  return (
    <div className="flex items-center gap-0.5" data-testid="star-rating">
      {stars.map(star => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          className={`transition-colors ${interactive ? 'cursor-pointer' : 'cursor-default'}`}
          onClick={() => interactive && onChange?.(star)}
          onMouseEnter={() => interactive && setHovered(star)}
          onMouseLeave={() => setHovered(0)}
        >
          <Star 
            className={`${sizeClasses[size]} ${
              star <= (hovered || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-600 text-gray-600'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Review Summary Badge
export const ReviewSummary = ({ email, compact = false }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API}/reviews/summary/${encodeURIComponent(email)}`);
        setSummary(res.data);
      } catch (error) {
        console.error('Error fetching review summary:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (email) fetchSummary();
  }, [email]);
  
  if (loading) {
    return <div className="animate-pulse bg-slate-700 rounded h-6 w-20"></div>;
  }
  
  if (!summary || summary.total_reviews === 0) {
    return (
      <span className="text-xs text-gray-500 italic">No reviews yet</span>
    );
  }
  
  if (compact) {
    return (
      <div className="flex items-center gap-1" data-testid="review-summary-compact">
        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
        <span className="text-sm font-medium text-white">{summary.avg_rating.toFixed(1)}</span>
        <span className="text-xs text-gray-400">({summary.total_reviews})</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-3" data-testid="review-summary">
      <div className="flex items-center gap-1">
        <StarRating rating={Math.round(summary.avg_rating)} size="sm" />
        <span className="text-sm font-medium text-white ml-1">{summary.avg_rating.toFixed(1)}</span>
      </div>
      <span className="text-xs text-gray-400">{summary.total_reviews} reviews</span>
      {summary.badges?.length > 0 && (
        <div className="flex gap-1">
          {summary.badges.includes('top_rated') && (
            <span className="px-1.5 py-0.5 text-[10px] bg-yellow-500/20 text-yellow-400 rounded-full">Top Rated</span>
          )}
          {summary.badges.includes('veteran') && (
            <span className="px-1.5 py-0.5 text-[10px] bg-purple-500/20 text-purple-400 rounded-full">Veteran</span>
          )}
        </div>
      )}
    </div>
  );
};

// Single Review Card
const ReviewCard = ({ review }) => {
  return (
    <div className="p-4 bg-slate-700/30 rounded-lg" data-testid="review-card">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-gray-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">{review.reviewer?.name || 'Anonymous'}</p>
            <p className="text-xs text-gray-500">{review.reviewer?.type === 'broker' ? 'Broker' : 'Driver'}</p>
          </div>
        </div>
        <StarRating rating={review.rating} size="sm" />
      </div>
      
      {review.comment && (
        <p className="text-sm text-gray-300 mb-2">{review.comment}</p>
      )}
      
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-1">
          {review.tags?.map((tag, i) => (
            <span key={i} className="px-1.5 py-0.5 text-[10px] bg-slate-600/50 text-gray-400 rounded">
              {tag}
            </span>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          {review.created_at ? new Date(review.created_at).toLocaleDateString() : ''}
        </span>
      </div>
    </div>
  );
};

// Write Review Dialog
export const WriteReviewDialog = ({ 
  reviewedEmail, 
  reviewedName, 
  reviewedType,
  reviewerEmail,
  reviewerName,
  reviewerType,
  loadId,
  onSuccess,
  trigger 
}) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  const tags = [
    'On Time', 'Great Communication', 'Professional', 'Fair Rates',
    'Reliable', 'Quick Payment', 'Easy to Work With', 'Recommended'
  ];
  
  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };
  
  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/create`, {
        reviewer_email: reviewerEmail,
        reviewer_name: reviewerName,
        reviewer_type: reviewerType,
        reviewed_email: reviewedEmail,
        reviewed_name: reviewedName,
        reviewed_type: reviewedType,
        load_id: loadId || null,
        rating,
        comment,
        tags: selectedTags
      });
      
      toast.success('Review submitted successfully!');
      setOpen(false);
      setRating(0);
      setComment('');
      setSelectedTags([]);
      onSuccess?.();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" variant="outline" className="gap-1">
            <MessageSquare className="w-3 h-3" /> Write Review
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white">Review {reviewedName}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-2">
          {/* Rating */}
          <div className="text-center">
            <p className="text-sm text-gray-400 mb-2">How would you rate your experience?</p>
            <div className="flex justify-center">
              <StarRating rating={rating} size="xl" interactive onChange={setRating} />
            </div>
          </div>
          
          {/* Tags */}
          <div>
            <Label className="text-gray-300 text-sm">What stood out? (optional)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {tags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-2 py-1 text-xs rounded-full transition-colors ${
                    selectedTags.includes(tag)
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
          
          {/* Comment */}
          <div>
            <Label className="text-gray-300 text-sm">Additional comments (optional)</Label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience..."
              className="w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm resize-none h-24"
            />
          </div>
          
          <Button 
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {submitting ? 'Submitting...' : (
              <>Submit Review <Send className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Full Reviews Section Component
const ReviewsSection = ({ email, showWriteReview = false, reviewerData }) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchReviews = async () => {
    try {
      const [reviewsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/reviews/${encodeURIComponent(email)}`),
        axios.get(`${API}/reviews/summary/${encodeURIComponent(email)}`)
      ]);
      
      setReviews(reviewsRes.data.reviews || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (email) fetchReviews();
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-slate-700 rounded w-1/3"></div>
            <div className="h-20 bg-slate-700 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="reviews-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          Reviews & Ratings
        </CardTitle>
        {showWriteReview && reviewerData && (
          <WriteReviewDialog
            reviewedEmail={email}
            reviewedName={reviewerData.reviewedName}
            reviewedType={reviewerData.reviewedType}
            reviewerEmail={reviewerData.reviewerEmail}
            reviewerName={reviewerData.reviewerName}
            reviewerType={reviewerData.reviewerType}
            onSuccess={fetchReviews}
          />
        )}
      </CardHeader>
      <CardContent>
        {/* Summary */}
        {summary && summary.total_reviews > 0 && (
          <div className="flex items-center gap-6 p-4 bg-slate-700/30 rounded-lg mb-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">{summary.avg_rating.toFixed(1)}</p>
              <StarRating rating={Math.round(summary.avg_rating)} size="sm" />
              <p className="text-xs text-gray-400 mt-1">{summary.total_reviews} reviews</p>
            </div>
            
            {summary.rating_distribution && (
              <div className="flex-1 space-y-1">
                {[5, 4, 3, 2, 1].map(star => {
                  const count = summary.rating_distribution[star] || 0;
                  const pct = summary.total_reviews > 0 ? (count / summary.total_reviews) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 w-3">{star}</span>
                      <Star className="w-3 h-3 text-yellow-400" />
                      <div className="flex-1 h-2 bg-slate-600 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-yellow-400 rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400 w-8">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Badges earned */}
            {summary.badges?.length > 0 && (
              <div className="space-y-1">
                {summary.badges.map(badge => (
                  <div key={badge} className="flex items-center gap-1 px-2 py-1 bg-slate-600/50 rounded-full">
                    {badge === 'top_rated' && <Award className="w-3 h-3 text-yellow-400" />}
                    {badge === 'veteran' && <TrendingUp className="w-3 h-3 text-purple-400" />}
                    <span className="text-xs text-gray-300 capitalize">{badge.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Reviews List */}
        {reviews.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No reviews yet</p>
            <p className="text-sm text-gray-500">Be the first to leave a review!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reviews.map((review, i) => (
              <ReviewCard key={review.id || i} review={review} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReviewsSection;
